import React, { Fragment, useContext, useEffect, useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import henceforthApi from '../../utils/henceforthApi';
import { GlobalContext, handleError } from '../../context/Provider';
import BreadCrumb from '../../components/common/BreadCrumb';
import authAction from '../../context/authAction';
import { Button } from '../../components/ui/button';
import {
  Landmark,
  User,
  CreditCard,
  Building2,
  Calendar as CalendarIcon,
  ShieldCheck,
  Upload,
  ArrowLeft,
  Save,
  CheckCircle2,
  Lock,
  FileText,
  X,
  AlertCircle,
  HelpCircle,
  Sparkles,
  Zap,
  Info,
  Check,
  Wifi,
  DollarSign,
  Clock,
  Eye,
  EyeOff,
} from 'lucide-react';

const BankDetails: React.FC = () => {
  const { language_data, authState, loading, setLoading, toastMessage, authDispatch } =
    useContext(GlobalContext);
  const navigate = useNavigate();

  const [state, setState] = useState({
    account_holder_name: '',
    account_number: '',
    routing_number: '',
    bank_name: '',
    ssn_last4_number: '',
    date_of_birth: '',
    document_file_key: '',
  });

  const [errors, setErrors] = useState<any>({});
  const [selectedFile, setSelectedFile] = useState<any>(null);
  const [showAccountNumber, setShowAccountNumber] = useState(false);

  const breadCrumbPath = [
    { name: `${language_data?.[authState.lang]?.common_home || 'Home'}`, url: `/`, active: '' },
    { name: `${language_data?.[authState.lang]?.common_profile || 'Profile'}`, url: `/profile`, active: '' },
    { name: `Bank Details`, url: ``, active: 'not-allowed' },
  ];

  useEffect(() => {
    if (authState?.is_bank_added) {
      fetchBankDetails();
    }
  }, [authState?.is_bank_added]);

  const fetchBankDetails = async () => {
    setLoading(true);
    try {
      const apiRes = await henceforthApi.Bank.get();
      const bankData = apiRes?.data?.data?.[0];

      if (bankData) {
        setState((prev: any) => ({
          ...prev,
          account_holder_name: bankData.account_holder_name || '',
          account_number: '',
          routing_number: bankData.routing_number || '',
          bank_name: bankData.bank_name || '',
          ssn_last4_number: bankData.account_number_last4 || '',
          date_of_birth: '',
          document_file_key: bankData.document_file_key || '',
        }));
      }
    } catch (err) {
      handleError(err, 'active', authDispatch);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setState({ ...state, [name]: value });
    setErrors({ ...errors, [name]: '' });
  };

  const handleFile = (e: any) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setErrors({ ...errors, document_file_key: '' });
    }
  };

  // Quick preset bank selector
  const selectPresetBank = (bankName: string) => {
    setState((prev) => ({ ...prev, bank_name: bankName }));
    if (errors.bank_name) {
      setErrors((prev: any) => ({ ...prev, bank_name: '' }));
    }
  };

  // Form completion progress
  const completionPercentage = useMemo(() => {
    let filled = 0;
    if (state.account_holder_name.trim()) filled++;
    if (state.bank_name.trim()) filled++;
    if (state.account_number.trim() || state.ssn_last4_number.trim()) filled++;
    if (state.routing_number.trim()) filled++;
    if (state.date_of_birth) filled++;
    if (state.document_file_key || selectedFile || authState?.is_bank_added) filled++;
    return Math.round((filled / 6) * 100);
  }, [state, selectedFile, authState?.is_bank_added]);

  const validate = () => {
    const newErrors: any = {};
    if (!state.account_holder_name.trim())
      newErrors.account_holder_name = 'Account holder name is required';
    if (!state.bank_name.trim()) newErrors.bank_name = 'Bank name is required';
    if (!state.account_number.trim() && !authState?.is_bank_added)
      newErrors.account_number = 'Account number is required';
    if (!state.routing_number.trim()) newErrors.routing_number = 'Routing number is required';
    if (!state.ssn_last4_number.trim())
      newErrors.ssn_last4_number = 'SSN last 4 digits are required';
    if (!state.date_of_birth) newErrors.date_of_birth = 'Date of birth is required';
    if (!state.document_file_key && !selectedFile && !authState?.is_bank_added)
      newErrors.document_file_key = 'Void check or verification document is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      let documentKey = state.document_file_key;
      if (selectedFile) {
        const uploadRes = await henceforthApi.Common.do_spaces_file_upload('file', selectedFile);
        documentKey = uploadRes.data.file_name;
      }

      const payload = {
        account_holder_name: state.account_holder_name.trim(),
        account_number: state.account_number.trim(),
        routing_number: state.routing_number.trim(),
        bank_name: state.bank_name.trim(),
        ssn_last4_number: state.ssn_last4_number.trim(),
        date_of_birth: state.date_of_birth,
        document_file_key: documentKey,
      };

      if (authState?.is_bank_added) {
        await henceforthApi.Bank.edit(payload);
        toastMessage('Bank details updated successfully');
      } else {
        await henceforthApi.Bank.add(payload);
        toastMessage('Bank details added successfully');
      }

      const profileRes = await henceforthApi.Auth.getProfile();
      authAction.initialiseProfile(profileRes.data)(authDispatch);

      navigate('/profile');
    } catch (err) {
      handleError(err, 'active', authDispatch);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fragment>
      {/* Standard Breadcrumb Bar */}
      <BreadCrumb pathNameDeclare={breadCrumbPath} />

      <div className="w-full px-4 sm:px-6 lg:px-8 py-4 space-y-5">
        {/* 1. Header Card */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white px-5 py-4 rounded-2xl border border-slate-200/80 shadow-xs">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight m-0">
                Bank & Payout Setup
              </h1>
            </div>
            <p className="text-xs sm:text-sm text-slate-500 font-medium mt-1 mb-0">
              Configure your verified business settlement account to receive automated weekly store sales transfers.
            </p>
          </div>
        </div>

        {/* 3. Main Split Container */}
        <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-start">

          {/* Right Column (7 Cols): Comprehensive Structured Form */}
          <div className="lg:col-span-12 space-y-1">
            {/* Account Credentials Card */}
            <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs p-6 space-y-5">
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-xl bg-sky-50 border border-sky-200 flex items-center justify-center text-[#1AA7DE]">
                    <Landmark className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-black text-slate-900 m-0">Depository Account Info</h3>
                    <span className="text-xs text-slate-400 font-medium">Verify your US depository account</span>
                  </div>
                </div>

                <span className="text-xs text-slate-400 font-semibold">
                  <span className="text-rose-500">*</span> Required
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Account Holder Name */}
                <div className="space-y-1 sm:col-span-2">
                  <label className="text-xs font-bold text-slate-700 block">
                    Account Beneficiary / Holder Full Name <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <User className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                      type="text"
                      name="account_holder_name"
                      value={state.account_holder_name}
                      onChange={handleChange}
                      placeholder="e.g. John Doe / Acme Inc"
                      className={`w-full pl-10 pr-3 py-2.5 text-xs sm:text-sm font-medium rounded-xl border ${errors.account_holder_name
                        ? 'border-rose-400 bg-rose-50/20'
                        : 'border-slate-200 bg-slate-50/50'
                        } focus:outline-none focus:ring-2 focus:ring-[#1AA7DE]/20 focus:border-[#1AA7DE] transition-all`}
                    />
                  </div>
                  {errors.account_holder_name && (
                    <p className="text-[11px] text-rose-500 font-semibold m-0">
                      {errors.account_holder_name}
                    </p>
                  )}
                </div>

                {/* Bank Name */}
                <div className="space-y-1 sm:col-span-2">
                  <label className="text-xs font-bold text-slate-700 block">
                    Bank Institution Name <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <Building2 className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                      type="text"
                      name="bank_name"
                      value={state.bank_name}
                      onChange={handleChange}
                      placeholder="e.g. JPMorgan Chase Bank, N.A."
                      className={`w-full pl-10 pr-3 py-2.5 text-xs sm:text-sm font-medium rounded-xl border ${errors.bank_name
                        ? 'border-rose-400 bg-rose-50/20'
                        : 'border-slate-200 bg-slate-50/50'
                        } focus:outline-none focus:ring-2 focus:ring-[#1AA7DE]/20 focus:border-[#1AA7DE] transition-all`}
                    />
                  </div>
                  {errors.bank_name && (
                    <p className="text-[11px] text-rose-500 font-semibold m-0">{errors.bank_name}</p>
                  )}
                </div>

                {/* Account Number */}
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-bold text-slate-700 block">
                      Account Number <span className="text-rose-500">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowAccountNumber(!showAccountNumber)}
                      className="text-[11px] text-[#1AA7DE] font-semibold hover:underline flex items-center gap-1 cursor-pointer"
                    >
                      {showAccountNumber ? (
                        <>
                          <EyeOff className="w-3 h-3" /> Hide
                        </>
                      ) : (
                        <>
                          <Eye className="w-3 h-3" /> Show
                        </>
                      )}
                    </button>
                  </div>
                  <div className="relative">
                    <CreditCard className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                      type={showAccountNumber ? 'text' : 'password'}
                      name="account_number"
                      value={state.account_number}
                      onChange={handleChange}
                      placeholder="Enter account number"
                      className={`w-full pl-10 pr-3 py-2.5 text-xs sm:text-sm font-mono font-medium rounded-xl border ${errors.account_number
                        ? 'border-rose-400 bg-rose-50/20'
                        : 'border-slate-200 bg-slate-50/50'
                        } focus:outline-none focus:ring-2 focus:ring-[#1AA7DE]/20 focus:border-[#1AA7DE] transition-all`}
                    />
                  </div>
                  {errors.account_number && (
                    <p className="text-[11px] text-rose-500 font-semibold m-0">
                      {errors.account_number}
                    </p>
                  )}
                </div>

                {/* Routing Number (ABA) */}
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-700 block">
                    Routing Number (9 Digits) <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <Landmark className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                      type="text"
                      maxLength={9}
                      name="routing_number"
                      value={state.routing_number}
                      onChange={handleChange}
                      placeholder="e.g. 122000496"
                      className={`w-full pl-10 pr-3 py-2.5 text-xs sm:text-sm font-mono font-medium rounded-xl border ${errors.routing_number
                        ? 'border-rose-400 bg-rose-50/20'
                        : 'border-slate-200 bg-slate-50/50'
                        } focus:outline-none focus:ring-2 focus:ring-[#1AA7DE]/20 focus:border-[#1AA7DE] transition-all`}
                    />
                  </div>
                  {errors.routing_number && (
                    <p className="text-[11px] text-rose-500 font-semibold m-0">
                      {errors.routing_number}
                    </p>
                  )}
                </div>

                {/* SSN Last 4 Digits */}
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-700 block">
                    SSN / Tax ID Last 4 Digits <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <ShieldCheck className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                      type="password"
                      maxLength={4}
                      name="ssn_last4_number"
                      value={state.ssn_last4_number}
                      onChange={handleChange}
                      placeholder="e.g. 1234"
                      className={`w-full pl-10 pr-3 py-2.5 text-xs sm:text-sm font-mono font-medium rounded-xl border ${errors.ssn_last4_number
                        ? 'border-rose-400 bg-rose-50/20'
                        : 'border-slate-200 bg-slate-50/50'
                        } focus:outline-none focus:ring-2 focus:ring-[#1AA7DE]/20 focus:border-[#1AA7DE] transition-all`}
                    />
                  </div>
                  {errors.ssn_last4_number && (
                    <p className="text-[11px] text-rose-500 font-semibold m-0">
                      {errors.ssn_last4_number}
                    </p>
                  )}
                </div>

                {/* Date of Birth */}
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-700 block">
                    Beneficiary Date of Birth <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <CalendarIcon className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                      type="date"
                      name="date_of_birth"
                      value={state.date_of_birth}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-3 py-2.5 text-xs sm:text-sm font-medium rounded-xl border ${errors.date_of_birth
                        ? 'border-rose-400 bg-rose-50/20'
                        : 'border-slate-200 bg-slate-50/50'
                        } focus:outline-none focus:ring-2 focus:ring-[#1AA7DE]/20 focus:border-[#1AA7DE] transition-all`}
                    />
                  </div>
                  {errors.date_of_birth && (
                    <p className="text-[11px] text-rose-500 font-semibold m-0">
                      {errors.date_of_birth}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Document Upload Card */}
            <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs p-6 space-y-4">
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-xl bg-purple-50 border border-purple-200 flex items-center justify-center text-purple-600">
                    <FileText className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-black text-slate-900 m-0">Account Verification Proof</h3>
                    <span className="text-xs text-slate-400 font-medium">
                      Upload a Void Check or Official Bank Statement
                    </span>
                  </div>
                </div>

                {state.document_file_key || selectedFile ? (
                  <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600 bg-emerald-50 px-2.5 py-0.5 rounded-full border border-emerald-200">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Document Attached
                  </span>
                ) : (
                  <span className="text-xs text-rose-500 font-semibold">* Required</span>
                )}
              </div>

              {/* Upload Dropzone */}
              <div
                className={`border-2 border-dashed rounded-2xl p-6 text-center transition-all ${errors.document_file_key
                  ? 'border-rose-400 bg-rose-50/20'
                  : 'border-slate-300 hover:border-[#1AA7DE] bg-slate-50/60'
                  }`}
              >
                <input
                  type="file"
                  id="bank_doc_input"
                  className="hidden"
                  accept="image/*"
                  onChange={handleFile}
                />

                {selectedFile || state.document_file_key ? (
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <div className="relative h-28 w-44 rounded-xl border border-slate-200 overflow-hidden bg-white shadow-md shrink-0">
                      <img
                        src={
                          selectedFile
                            ? URL.createObjectURL(selectedFile)
                            : `${henceforthApi.API_FILE_ROOT_ORIGINAL}${state.document_file_key}`
                        }
                        alt="Bank Document Preview"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="text-center sm:text-left space-y-2">
                      <div>
                        <span className="text-xs font-bold text-slate-800 block">
                          {selectedFile ? selectedFile.name : 'Verified Settlement Proof'}
                        </span>
                        <span className="text-[11px] text-slate-400 font-medium">
                          File successfully loaded for verification
                        </span>
                      </div>
                      <label
                        htmlFor="bank_doc_input"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-xs font-bold text-[#1AA7DE] shadow-2xs transition-colors cursor-pointer"
                      >
                        <Upload className="w-3.5 h-3.5" />
                        Replace File
                      </label>
                    </div>
                  </div>
                ) : (
                  <label
                    htmlFor="bank_doc_input"
                    className="cursor-pointer flex flex-col items-center gap-2.5 py-3"
                  >
                    <div className="w-14 h-14 rounded-2xl bg-sky-50 border border-sky-200 flex items-center justify-center text-[#1AA7DE] shadow-2xs group-hover:scale-110 transition-transform">
                      <Upload className="w-7 h-7" />
                    </div>
                    <div>
                      <span className="text-sm font-bold text-slate-800 block">
                        Click or drag document to upload
                      </span>
                      <span className="text-xs text-slate-400 font-medium mt-0.5 block">
                        Upload an official void check or direct deposit bank statement (PNG, JPG, PDF up to 10MB)
                      </span>
                    </div>
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-xl bg-slate-200/70 text-slate-700 text-xs font-semibold">
                      Browse Files
                    </span>
                  </label>
                )}
              </div>
              {errors.document_file_key && (
                <p className="text-[11px] text-rose-500 font-semibold m-0">
                  {errors.document_file_key}
                </p>
              )}
            </div>

            {/* Bottom Form Action Buttons */}
            <div className="flex items-center justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/profile')}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="default"
                disabled={loading}
                className="px-6 min-w-[160px] shadow-sm"
              >
                <Save className="w-4 h-4 me-2" />
                {loading ? 'Saving...' : authState?.is_bank_added ? 'Update Bank Details' : 'Save & Activate Payouts'}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </Fragment>
  );
};

export default BankDetails;
